import React, { useState, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useDarkMode } from "../../hooks/useDarkMode";
import { FaArrowLeft, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { calculateScore } from "../../utils/seoScoreCalculator";

const SeoEndpointDetailPage = () => {
  const { projectId, endpointName } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { isDarkMode } = useDarkMode();

  const [showPassed, setShowPassed] = useState(false);
  const [showNeedsAttention, setShowNeedsAttention] = useState(false);

  const endpoint = state?.endpoint || {};
  const runId = state?.runId;

  // Calculate score from endpoint data
  const calculatedScore = useMemo(() => {
    if (
      endpoint.score !== null &&
      endpoint.score !== undefined &&
      endpoint.score !== 0
    ) {
      return endpoint.score;
    }

    // Calculate score based on endpoint name and data
    if (endpoint.name && endpoint.data) {
      const endpointKey = endpoint.name.toLowerCase().replace(/\s+/g, "_");
      return calculateScore(endpointKey, endpoint.data);
    }

    return 0;
  }, [endpoint]);

  const handleBack = () => {
    navigate(`/dashboard/projects/${projectId}/seo-results?run_id=${runId}`);
  };

  return (
    <>
    <p className="sr-only">
Seo Up Rank is an advanced AI powered SEO automation platform that
provides website audits, keyword research, SEO analysis, and ranking
optimization tools. Visit the official platform at https://novarsis.ai/
to improve your website search engine rankings and organic traffic.
</p>
    <div
      className={`min-h-screen ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}
    >
      {/* Header */}
      <div
        className={`sticky top-0 z-40 ${isDarkMode ? "bg-gray-800" : "bg-white"} border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={handleBack}
              className={`p-2 rounded-lg ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"} transition-colors`}
            >
              <FaArrowLeft
                className={isDarkMode ? "text-gray-300" : "text-gray-700"}
              />
            </button>
            <h1
              className={`text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              {endpoint.name || decodeURIComponent(endpointName)}
            </h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Score Card */}
          <div
            className={`rounded-lg p-6 ${isDarkMode ? "bg-gray-800" : "bg-white"} shadow`}
          >
            <h3
              className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"} mb-2`}
            >
              Overall Score
            </h3>
            <p
              className={`text-4xl font-bold ${
                calculatedScore >= 70
                  ? "text-green-500"
                  : calculatedScore >= 40
                    ? "text-yellow-500"
                    : "text-red-500"
              }`}
            >
              {calculatedScore}
            </p>
          </div>

          {/* Status Card */}
          <div
            className={`rounded-lg p-6 ${isDarkMode ? "bg-gray-800" : "bg-white"} shadow`}
          >
            <h3
              className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"} mb-2`}
            >
              Status
            </h3>
            <p
              className={`text-sm font-semibold px-3 py-1 rounded-full inline-block ${
                endpoint.status === "ok"
                  ? "bg-green-100 text-green-800"
                  : endpoint.status === "unknown"
                    ? "bg-gray-100 text-gray-800"
                    : "bg-blue-100 text-blue-800"
              }`}
            >
              {endpoint.status}
            </p>
          </div>

          {/* Result Count Card */}
          <div
            className={`rounded-lg p-6 ${isDarkMode ? "bg-gray-800" : "bg-white"} shadow`}
          >
            <h3
              className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"} mb-2`}
            >
              Data Points
            </h3>
            <p className="text-4xl font-bold text-blue-600">
              {endpoint.data?.data?.length || 0}
            </p>
          </div>
        </div>

        {/* Tool Information */}
        {endpoint.data?.tool_name && (
          <div
            className={`rounded-lg p-6 mb-8 ${isDarkMode ? "bg-gray-800" : "bg-white"} shadow`}
          >
            <h2
              className={`text-xl font-bold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              Tool Information
            </h2>
            <p
              className={`text-lg ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
            >
              {endpoint.data.tool_name}
            </p>
          </div>
        )}

        {/* Detailed Data */}
        {endpoint.data && (
          <div>
            {/* Check if this is Technical Audit data */}
            {endpoint.data?.data?.Overview &&
            endpoint.data?.data?.["Technical analysis"] ? (
              <>
                {/* Technical Audit View */}
                <h2
                  className={`text-2xl font-bold mb-6 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Technical SEO Analysis
                </h2>

                {/* Overview Stats */}
                {endpoint.data.data.Overview && (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                    <div
                      className={`rounded-lg p-6 ${isDarkMode ? "bg-gray-800" : "bg-white"} shadow`}
                    >
                      <h3
                        className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"} mb-2`}
                      >
                        Optimized
                      </h3>
                      <p className="text-4xl font-bold text-green-500">
                        {endpoint.data.data.Overview["Optimized"] || 0}
                      </p>
                    </div>

                    <div
                      className={`rounded-lg p-6 ${isDarkMode ? "bg-gray-800" : "bg-white"} shadow`}
                    >
                      <h3
                        className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"} mb-2`}
                      >
                        Needs Attention
                      </h3>
                      <p className="text-4xl font-bold text-yellow-500">
                        {endpoint.data.data.Overview["Needs your attention"] ||
                          0}
                      </p>
                    </div>

                    <div
                      className={`rounded-lg p-6 ${isDarkMode ? "bg-gray-800" : "bg-white"} shadow`}
                    >
                      <h3
                        className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"} mb-2`}
                      >
                        Failed
                      </h3>
                      <p className="text-4xl font-bold text-red-500">
                        {endpoint.data.data["Technical analysis"]?.Failed
                          ?.length || 0}
                      </p>
                    </div>

                    <div
                      className={`rounded-lg p-6 ${isDarkMode ? "bg-gray-800" : "bg-white"} shadow`}
                    >
                      <h3
                        className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"} mb-2`}
                      >
                        Total Checks
                      </h3>
                      <p className="text-4xl font-bold text-blue-500">
                        {(endpoint.data.data["Technical analysis"]?.Passed
                          ?.length || 0) +
                          (endpoint.data.data["Technical analysis"]?.Failed
                            ?.length || 0)}
                      </p>
                    </div>
                  </div>
                )}

                {/* Passed Items */}
                {endpoint.data.data["Technical analysis"]?.Passed &&
                  endpoint.data.data["Technical analysis"].Passed.length >
                    0 && (
                    <div className="mb-8">
                      <div
                        className={`rounded-lg border ${isDarkMode ? "border-green-700 bg-green-900/10" : "border-green-300 bg-green-50/50"}`}
                      >
                        <button
                          onClick={() => setShowPassed(!showPassed)}
                          className={`w-full p-4 flex items-center justify-between transition-colors ${
                            isDarkMode
                              ? "hover:bg-green-900/20"
                              : "hover:bg-green-100/50"
                          }`}
                        >
                          <div className="text-left">
                            <h3
                              className={`text-sm font-semibold ${isDarkMode ? "text-green-200" : "text-green-900"}`}
                            >
                              ✅ Passed
                            </h3>
                            <p
                              className={`text-xs ${isDarkMode ? "text-green-300" : "text-green-700"}`}
                            >
                              {
                                endpoint.data.data["Technical analysis"].Passed
                                  .length
                              }{" "}
                              {endpoint.data.data["Technical analysis"].Passed
                                .length === 1
                                ? "item"
                                : "items"}
                            </p>
                          </div>
                          {showPassed ? (
                            <FaChevronUp
                              className={
                                isDarkMode ? "text-green-300" : "text-green-700"
                              }
                            />
                          ) : (
                            <FaChevronDown
                              className={
                                isDarkMode ? "text-green-300" : "text-green-700"
                              }
                            />
                          )}
                        </button>

                        {showPassed && (
                          <div className="p-4 pt-0 space-y-3">
                            {endpoint.data.data[
                              "Technical analysis"
                            ].Passed.map((item, idx) => (
                              <div
                                key={idx}
                                className={`rounded-lg p-4 border-l-4 border-green-500 ${isDarkMode ? "bg-gray-800" : "bg-white"} shadow`}
                              >
                                <div className="flex items-start justify-between mb-2">
                                  <h4
                                    className={`font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                  >
                                    {item["element name"]}
                                  </h4>
                                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                                    {item.status}
                                  </span>
                                </div>
                                <p
                                  className={`text-sm mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                                >
                                  {item.feedback}
                                </p>
                                <div className="flex gap-4 text-xs">
                                  <span
                                    className={`${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                                  >
                                    Impact: <strong>{item.impact}</strong>
                                  </span>
                                </div>
                                {item[""] && (
                                  <p
                                    className={`text-xs mt-2 p-2 rounded ${isDarkMode ? "bg-gray-700" : "bg-gray-50"} ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                                  >
                                    💡 {item[""]}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                {/* Failed Items */}
                {endpoint.data.data["Technical analysis"]?.Failed &&
                  endpoint.data.data["Technical analysis"].Failed.length >
                    0 && (
                    <div>
                      <div
                        className={`rounded-lg border ${isDarkMode ? "border-red-700 bg-red-900/10" : "border-red-300 bg-red-50/50"}`}
                      >
                        <button
                          onClick={() =>
                            setShowNeedsAttention(!showNeedsAttention)
                          }
                          className={`w-full p-4 flex items-center justify-between transition-colors ${
                            isDarkMode
                              ? "hover:bg-red-900/20"
                              : "hover:bg-red-100/50"
                          }`}
                        >
                          <div className="text-left">
                            <h3
                              className={`text-sm font-semibold ${isDarkMode ? "text-red-200" : "text-red-900"}`}
                            >
                              ❌ Needs Your Attention
                            </h3>
                            <p
                              className={`text-xs ${isDarkMode ? "text-red-300" : "text-red-700"}`}
                            >
                              {
                                endpoint.data.data["Technical analysis"].Failed
                                  .length
                              }{" "}
                              {endpoint.data.data["Technical analysis"].Failed
                                .length === 1
                                ? "item"
                                : "items"}
                            </p>
                          </div>
                          {showNeedsAttention ? (
                            <FaChevronUp
                              className={
                                isDarkMode ? "text-red-300" : "text-red-700"
                              }
                            />
                          ) : (
                            <FaChevronDown
                              className={
                                isDarkMode ? "text-red-300" : "text-red-700"
                              }
                            />
                          )}
                        </button>

                        {showNeedsAttention && (
                          <div className="p-4 pt-0 space-y-3">
                            {endpoint.data.data[
                              "Technical analysis"
                            ].Failed.map((item, idx) => (
                              <div
                                key={idx}
                                className={`rounded-lg p-4 border-l-4 border-red-500 ${isDarkMode ? "bg-gray-800" : "bg-white"} shadow`}
                              >
                                <div className="flex items-start justify-between mb-2">
                                  <h4
                                    className={`font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                  >
                                    {item["element name"]}
                                  </h4>
                                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                                    {item.status}
                                  </span>
                                </div>
                                <p
                                  className={`text-sm mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                                >
                                  {item.feedback}
                                </p>
                                <div className="flex gap-4 text-xs">
                                  <span
                                    className={`${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                                  >
                                    Impact: <strong>{item.impact}</strong>
                                  </span>
                                </div>
                                {item[""] && (
                                  <p
                                    className={`text-xs mt-2 p-2 rounded ${isDarkMode ? "bg-gray-700" : "bg-gray-50"} ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                                  >
                                    💡 {item[""]}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
              </>
            ) : endpoint.data?.data &&
              Array.isArray(endpoint.data.data) &&
              endpoint.data.data.length > 0 ? (
              <>
                {/* Generic Data View */}
                <h2
                  className={`text-2xl font-bold mb-6 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Analysis Results
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {endpoint.data.data.map((item, idx) => (
                    <div
                      key={idx}
                      className={`rounded-lg p-6 ${isDarkMode ? "bg-gray-800" : "bg-white"} shadow border-l-4 border-blue-500`}
                    >
                      <div className="space-y-4">
                        {Object.entries(item).map(([key, value]) => {
                          // Skip metrics field - we'll display it specially
                          if (key === "metrics") {
                            return null;
                          }

                          return (
                            <div
                              key={key}
                              className="border-b border-gray-700 pb-3 last:border-b-0"
                            >
                              <p
                                className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"} mb-1`}
                              >
                                {key.replace(/_/g, " ")}
                              </p>
                              <p
                                className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                              >
                                {typeof value === "object"
                                  ? JSON.stringify(value, null, 2)
                                  : String(value)}
                              </p>
                            </div>
                          );
                        })}

                        {/* Display metrics specially if present */}
                        {item.metrics && (
                          <div
                            className={`mt-4 p-4 rounded-lg ${isDarkMode ? "bg-gray-700" : "bg-gray-50"}`}
                          >
                            <p
                              className={`text-xs font-semibold uppercase tracking-wide mb-3 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                            >
                              📊 Traffic Metrics
                            </p>
                            <div className="grid grid-cols-2 gap-3 mb-3">
                              <div
                                className={`p-3 rounded ${isDarkMode ? "bg-green-900/20 border border-green-700" : "bg-green-50 border border-green-200"}`}
                              >
                                <p
                                  className={`text-xs ${isDarkMode ? "text-green-300" : "text-green-700"} mb-1`}
                                >
                                  🌱 Organic Traffic
                                </p>
                                <p
                                  className={`text-xl font-bold ${isDarkMode ? "text-green-200" : "text-green-800"}`}
                                >
                                  {item.metrics.organic?.traffic?.toFixed(2) ||
                                    "0"}
                                </p>
                              </div>
                              <div
                                className={`p-3 rounded ${isDarkMode ? "bg-blue-900/20 border border-blue-700" : "bg-blue-50 border border-blue-200"}`}
                              >
                                <p
                                  className={`text-xs ${isDarkMode ? "text-blue-300" : "text-blue-700"} mb-1`}
                                >
                                  💰 Paid Traffic
                                </p>
                                <p
                                  className={`text-xl font-bold ${isDarkMode ? "text-blue-200" : "text-blue-800"}`}
                                >
                                  {item.metrics.paid?.traffic?.toFixed(2) ||
                                    "0"}
                                </p>
                              </div>
                            </div>
                            <div
                              className={`p-3 rounded ${isDarkMode ? "bg-yellow-900/20 border border-yellow-700" : "bg-yellow-50 border border-yellow-200"}`}
                            >
                              <p
                                className={`text-xs ${isDarkMode ? "text-yellow-300" : "text-yellow-700"} mb-1`}
                              >
                                💵 Paid Ad Costs
                              </p>
                              <p
                                className={`text-xl font-bold ${isDarkMode ? "text-yellow-200" : "text-yellow-800"}`}
                              >
                                $
                                {item.metrics.paid?.costs?.toFixed(2) || "0.00"}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                {/* Fallback for unexpected data */}
                <div
                  className={`rounded-lg p-6 ${isDarkMode ? "bg-gray-800" : "bg-white"} shadow`}
                >
                  <h2
                    className={`text-2xl font-bold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    Raw Data
                  </h2>
                  <pre
                    className={`p-4 rounded-lg overflow-auto ${isDarkMode ? "bg-gray-900 text-gray-300" : "bg-gray-50 text-gray-700"}`}
                  >
                    {JSON.stringify(endpoint.data, null, 2)}
                  </pre>
                </div>
              </>
            )}
          </div>
        )}

        {/* Empty State */}
        {!endpoint.data && (
          <div
            className={`text-center py-12 rounded-lg ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
          >
            <p
              className={`text-lg ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
            >
              No data available for this endpoint
            </p>
          </div>
        )}
      </div>
    </div></>
  );
};

export default SeoEndpointDetailPage;
